import type { BROWSER_FAMILIES, ClientContext, OS_FAMILIES } from "@esi/shared";
import { reduceLocale } from "@esi/shared";

type Browser = (typeof BROWSER_FAMILIES)[number];
type Os = (typeof OS_FAMILIES)[number];

interface UaData {
  brands?: readonly { brand: string; version: string }[];
  platform?: string;
}

const BRANDS: [RegExp, Browser][] = [
  [/edge/i, "edge"],
  [/opera/i, "opera"],
  [/samsung/i, "samsung"],
  [/chrome/i, "chrome"],
];

/** The browser family and major version: from `userAgentData`, else a reduced UA parse. */
function browserOf(ua: string, data: UaData | undefined): { browser: Browser; major?: number } {
  for (const [pattern, browser] of BRANDS) {
    const brand = data?.brands?.find((b) => pattern.test(b.brand));
    if (brand) {
      return { browser, major: Number.parseInt(brand.version, 10) || undefined };
    }
  }
  const rules: [RegExp, Browser][] = [
    [/Edg(?:e|A|iOS)?\/(\d+)/, "edge"],
    [/(?:OPR|Opera)\/(\d+)/, "opera"],
    [/SamsungBrowser\/(\d+)/, "samsung"],
    [/(?:Firefox|FxiOS)\/(\d+)/, "firefox"],
    [/(?:Chrome|CriOS)\/(\d+)/, "chrome"],
    [/Version\/(\d+)[^ ]* (?:Mobile\/\S+ )?Safari\//, "safari"],
  ];
  for (const [pattern, browser] of rules) {
    const match = pattern.exec(ua);
    if (match) {
      return { browser, major: Number(match[1]) };
    }
  }
  return { browser: "other" };
}

function osOf(ua: string, data: UaData | undefined): Os {
  const platform = `${data?.platform ?? ""} ${ua}`;
  if (/iPhone|iPad|iPod/.test(platform)) {
    return "ios";
  }
  if (/Android/i.test(platform)) {
    return "android";
  }
  if (/CrOS|Chrome OS/i.test(platform)) {
    return "chromeos";
  }
  if (/Windows/i.test(platform)) {
    return "windows";
  }
  if (/mac ?OS|Macintosh/i.test(platform)) {
    return "macos";
  }
  if (/Linux/i.test(platform)) {
    return "linux";
  }
  return "other";
}

/** The viewport class: `s` under 640 px, `m` under 1024 px, else `l`. */
export const viewportOf = (width: number): ClientContext["viewport"] =>
  width < 640 ? "s" : width < 1024 ? "m" : "l";

/** The page's coarse context: families and classes, never the raw user agent. */
export function clientContext(
  nav: Pick<Navigator, "userAgent" | "language"> & { userAgentData?: UaData } = navigator,
  width: number = window.innerWidth,
): ClientContext {
  const { browser, major } = browserOf(nav.userAgent, nav.userAgentData);
  const locale = reduceLocale(nav.language);
  return {
    browser,
    ...(major !== undefined && major >= 0 && major <= 999 && { browserMajor: major }),
    os: osOf(nav.userAgent, nav.userAgentData),
    ...(locale !== undefined && { locale }),
    viewport: viewportOf(width),
  };
}
